import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Field — a labelled input with restrained styling.
 * Wraps <label> + <input>. Optional leading icon, error text, hint.
 *
 * When type="password", a trailing Eye / EyeOff toggle is rendered inside
 * the input. The toggle is a type="button" so it never submits the form,
 * preserves the entered value, and has an accessible aria-label.
 */
export function Field({
  id,
  label,
  hint,
  error,
  leadingIcon,
  type = 'text',
  required = false,
  value,
  onChange,
  ...rest
}) {
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const errorMode = Boolean(error);
  const isPassword = type === 'password';
  const effectiveType = isPassword && visible ? 'text' : type;

  const borderColour = errorMode
    ? 'var(--diis-danger)'
    : focused
    ? 'var(--diis-accent)'
    : 'var(--diis-hairline-strong)';

  const shadow = errorMode
    ? '0 0 0 3px rgba(138, 41, 41, 0.14)'
    : focused
    ? 'var(--diis-shadow-focus)'
    : 'none';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <label
          htmlFor={id}
          style={{
            fontFamily: 'var(--diis-font-sans)',
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--diis-ink-muted)',
            letterSpacing: '0.01em',
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--diis-danger)', marginLeft: 4 }}>*</span>}
        </label>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--diis-surface)',
          border: `1px solid ${borderColour}`,
          borderRadius: 'var(--diis-radius)',
          transition: 'border-color var(--diis-motion), box-shadow var(--diis-motion)',
          boxShadow: shadow,
          padding: '0 12px',
        }}
      >
        {leadingIcon && (
          <span
            style={{
              display: 'inline-flex',
              color: focused ? 'var(--diis-accent)' : 'var(--diis-ink-subtle)',
              marginRight: 8,
              transition: 'color var(--diis-motion)',
            }}
          >
            {leadingIcon}
          </span>
        )}
        <input
          id={id}
          type={effectiveType}
          value={value}
          onChange={onChange}
          required={required}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            padding: '11px 0',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'var(--diis-font-sans)',
            fontSize: '15px',
            color: 'var(--diis-ink)',
            letterSpacing: '-0.005em',
          }}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            data-testid={`${id || 'password'}-visibility-toggle`}
            tabIndex={-1}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 8,
              padding: 4,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: focused ? 'var(--diis-accent)' : 'var(--diis-ink-subtle)',
              transition: 'color var(--diis-motion)',
            }}
          >
            {visible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>

      {(hint || errorMode) && (
        <span
          style={{
            fontSize: '12px',
            color: errorMode ? 'var(--diis-danger)' : 'var(--diis-ink-subtle)',
            letterSpacing: '0.005em',
          }}
        >
          {errorMode ? error : hint}
        </span>
      )}
    </div>
  );
}
